/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { TeralCore } from "../core.js";
import { encodeJSON } from "../lib/encodings.js";
import { matchStatusCode } from "../lib/http.js";
import * as M from "../lib/matchers.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import * as components from "../models/components/index.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import * as errors from "../models/errors/index.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { TeralError } from "../models/errors/teralerror.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

/**
 * Verify domain
 *
 * @remarks
 * Restart verification for a custom domain.
 *
 * Address the domain by its ID or by its name. Names are unique per workspace, so
 * `api.acme.com` is enough.
 *
 * Call this after you correct the DNS records of a domain that shows `failed`, or to give a
 * `pending` domain a new 24-hour verification period. The domain goes back to `pending` and
 * the 24-hour period starts again.
 *
 * The endpoint returns when Teral accepts the retry. Poll `domains.getDomain` for the result.
 *
 * A domain that is already `verified` returns a 412.
 *
 * **Required Permissions**
 *
 * Your root key must have one of the following permissions:
 * - `environment.*.verify_domain` (to verify domains in any environment)
 * - `environment.<environment_id>.verify_domain` (to verify domains in a specific environment)
 *
 * If set, this operation will use {@link Security.apiKey} from the global security.
 */
export function domainsVerifyDomain(
  client: TeralCore,
  request: components.V1DomainsVerifyDomainRequestBody,
  options?: RequestOptions,
): APIPromise<
  Result<
    components.V1DomainsVerifyDomainResponseBody,
    | errors.BadRequestErrorResponse
    | errors.UnauthorizedErrorResponse
    | errors.ForbiddenErrorResponse
    | errors.NotFoundErrorResponse
    | errors.PreconditionFailedErrorResponse
    | errors.TooManyRequestsErrorResponse
    | errors.InternalServerErrorResponse
    | TeralError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >
> {
  return new APIPromise($do(
    client,
    request,
    options,
  ));
}

async function $do(
  client: TeralCore,
  request: components.V1DomainsVerifyDomainRequestBody,
  options?: RequestOptions,
): Promise<
  [
    Result<
      components.V1DomainsVerifyDomainResponseBody,
      | errors.BadRequestErrorResponse
      | errors.UnauthorizedErrorResponse
      | errors.ForbiddenErrorResponse
      | errors.NotFoundErrorResponse
      | errors.PreconditionFailedErrorResponse
      | errors.TooManyRequestsErrorResponse
      | errors.InternalServerErrorResponse
      | TeralError
      | ResponseValidationError
      | ConnectionError
      | RequestAbortedError
      | RequestTimeoutError
      | InvalidRequestError
      | UnexpectedClientError
      | SDKValidationError
    >,
    APICall,
  ]
> {
  const parsed = safeParse(
    request,
    (value) =>
      components.V1DomainsVerifyDomainRequestBody$outboundSchema.parse(value),
    "Input validation failed",
  );
  if (!parsed.ok) {
    return [parsed, { status: "invalid" }];
  }
  const payload = parsed.value;
  const body = encodeJSON("body", payload, { explode: true });

  const path = pathToFunc("/v1/domains.verifyDomain")();

  const headers = new Headers(compactMap({
    "Content-Type": "application/json",
    Accept: "application/json",
  }));

  const secConfig = await extractSecurity(client._options.apiKey);
  const securityInput = secConfig == null ? {} : { apiKey: secConfig };
  const requestSecurity = resolveGlobalSecurity(securityInput, [0]);

  const context = {
    options: client._options,
    baseURL: options?.serverURL ?? client._baseURL ?? "",
    operationID: "domains.verifyDomain",
    oAuth2Scopes: null,

    resolvedSecurity: requestSecurity,

    securitySource: client._options.apiKey,
    retryConfig: options?.retries
      || client._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || ["429", "500", "502", "503", "504"],
  };

  const requestRes = client._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path,
    headers: headers,
    body: body,
    userAgent: client._options.userAgent,
    timeoutMs: options?.timeoutMs || client._options.timeoutMs || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req = requestRes.value;

  const doResult = await client._do(req, {
    context,
    isErrorStatusCode: (statusCode: number) =>
      matchStatusCode({ status: statusCode } as Response, ["4XX", "5XX"]),
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req }];
  }
  const response = doResult.value;

  const responseFields = {
    HttpMeta: { Response: response, Request: req },
  };

  const [result] = await M.match<
    components.V1DomainsVerifyDomainResponseBody,
    | errors.BadRequestErrorResponse
    | errors.UnauthorizedErrorResponse
    | errors.ForbiddenErrorResponse
    | errors.NotFoundErrorResponse
    | errors.PreconditionFailedErrorResponse
    | errors.TooManyRequestsErrorResponse
    | errors.InternalServerErrorResponse
    | TeralError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >(
    M.json(202, components.V1DomainsVerifyDomainResponseBody$inboundSchema),
    M.jsonErr(400, errors.BadRequestErrorResponse$inboundSchema),
    M.jsonErr(401, errors.UnauthorizedErrorResponse$inboundSchema),
    M.jsonErr(403, errors.ForbiddenErrorResponse$inboundSchema),
    M.jsonErr(404, errors.NotFoundErrorResponse$inboundSchema),
    M.jsonErr(412, errors.PreconditionFailedErrorResponse$inboundSchema),
    M.jsonErr(429, errors.TooManyRequestsErrorResponse$inboundSchema),
    M.jsonErr(500, errors.InternalServerErrorResponse$inboundSchema),
    M.fail("4XX"),
    M.fail("5XX"),
  )(response, req, { extraFields: responseFields });
  if (!result.ok) {
    return [result, { status: "complete", request: req, response }];
  }

  return [result, { status: "complete", request: req, response }];
}
